import { useMemo, useState } from "react";
import { useWarehouseInventory, type ReceivingRecord, type JobAllocation } from "@/data/WarehouseInventoryContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Package, CheckCircle2, AlertTriangle, Truck, Flag, ShieldCheck, XCircle } from "lucide-react";
import type { Job } from "@/data/types";

function getJobDisplayRef(job: Job): string {
  if (typeof job.jobNumber === "string" && job.jobNumber.trim()) return job.jobNumber.trim();
  return job.id;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
}

export function computeJobMaterialStatus(
  receivingRecords: ReceivingRecord[],
  allocations: JobAllocation[],
  jobId: string
): { materialStatus: "Not Ordered" | "Ordered" | "Partially Received" | "Fully Received" | "Staged"; installReady: boolean; totalItems: number; receivedItems: number; allocatedItems: number; readyItems: number } {
  const jobReceiving = receivingRecords.filter((r) => r.jobId === jobId);
  const jobAllocations = allocations.filter((a) => a.jobId === jobId);

  const totalItems = jobReceiving.length;
  if (totalItems === 0) {
    return { materialStatus: "Not Ordered", installReady: false, totalItems: 0, receivedItems: 0, allocatedItems: 0, readyItems: 0 };
  }

  const receivedItems = jobReceiving.filter((r) => r.status === "Fully Received").length;
  const allocatedItems = jobAllocations.filter((a) => a.status === "Reserved" || a.status === "Ready for Install").length;
  const readyItems = jobAllocations.filter((a) => a.status === "Ready for Install").length;

  let materialStatus: "Not Ordered" | "Ordered" | "Partially Received" | "Fully Received" | "Staged";
  if (readyItems >= totalItems) {
    materialStatus = "Staged";
  } else if (receivedItems >= totalItems) {
    materialStatus = "Fully Received";
  } else if (receivedItems > 0) {
    materialStatus = "Partially Received";
  } else {
    materialStatus = "Ordered";
  }

  const installReady = readyItems >= totalItems && totalItems > 0;

  return { materialStatus, installReady, totalItems, receivedItems, allocatedItems, readyItems };
}

export function JobMaterialsModal({ open, onOpenChange, job }: Props) {
  const { receivingRecords, jobAllocations, getAllocationsByJob } = useWarehouseInventory();
  const [flagMode, setFlagMode] = useState(false);
  const [flagNotes, setFlagNotes] = useState("");

  const jobReceiving = useMemo(() => receivingRecords.filter((r) => r.jobId === job.id), [receivingRecords, job.id]);
  const allocs = useMemo(() => getAllocationsByJob(job.id), [getAllocationsByJob, job.id]);
  const status = useMemo(() => computeJobMaterialStatus(receivingRecords, jobAllocations, job.id), [receivingRecords, jobAllocations, job.id]);

  const materialLabel = job.projectConfig?.bathroomMaterials?.[0] ?? "";
  const pt = job.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : job.projectType;
  const productLabel = [materialLabel, pt].filter(Boolean).join(" ");

  const handleFlag = () => {
    if (!flagNotes.trim()) { toast.error("Please describe the missing items"); return; }
    toast.warning("Missing items flagged — Operations notified");
    setFlagMode(false); setFlagNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" /> Job Materials — {job.customerName}
          </DialogTitle>
        </DialogHeader>

        {/* Job Header */}
        <div className="bg-muted/50 rounded-md p-3 space-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold">{productLabel || job.projectType}</p>
              <p className="text-[10px] text-muted-foreground">{getJobDisplayRef(job)} · {job.address.city}, {job.address.state}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={status.materialStatus === "Staged" ? "default" : "outline"} className="text-[9px]">
                {status.materialStatus}
              </Badge>
              {status.installReady ? (
                <Badge className="text-[9px] bg-success/15 text-success border-success/30 gap-1">
                  <ShieldCheck className="h-2.5 w-2.5" /> Ready for Install
                </Badge>
              ) : (
                <Badge variant="destructive" className="text-[9px] gap-1">
                  <XCircle className="h-2.5 w-2.5" /> Not Ready
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="grid grid-cols-4 gap-3 text-center">
          <div className="bg-muted/30 rounded p-2">
            <p className="text-lg font-bold">{status.totalItems}</p>
            <p className="text-[10px] text-muted-foreground">Total SKUs</p>
          </div>
          <div className="bg-muted/30 rounded p-2">
            <p className="text-lg font-bold">{status.receivedItems}</p>
            <p className="text-[10px] text-muted-foreground">Received</p>
          </div>
          <div className="bg-muted/30 rounded p-2">
            <p className="text-lg font-bold">{status.allocatedItems}</p>
            <p className="text-[10px] text-muted-foreground">Allocated</p>
          </div>
          <div className={`rounded p-2 ${status.installReady ? "bg-success/10" : "bg-warning/10"}`}>
            <p className="text-lg font-bold">{status.readyItems}</p>
            <p className="text-[10px] text-muted-foreground">Ready</p>
          </div>
        </div>

        {/* Receiving Records */}
        {jobReceiving.length > 0 && (
          <div>
            <h3 className="text-xs font-bold mb-1 flex items-center gap-1"><Truck className="h-3 w-3" /> Receiving Status</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">SKU</TableHead>
                  <TableHead className="text-[10px]">Product</TableHead>
                  <TableHead className="text-[10px]">PO</TableHead>
                  <TableHead className="text-[10px] text-right">Exp.</TableHead>
                  <TableHead className="text-[10px] text-right">Rcvd</TableHead>
                  <TableHead className="text-[10px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobReceiving.map((r) => (
                  <TableRow key={r.id} className={r.discrepancy ? "bg-destructive/5" : ""}>
                    <TableCell className="text-[10px] font-mono">{r.sku}</TableCell>
                    <TableCell className="text-xs">{r.productName}</TableCell>
                    <TableCell className="text-[10px] font-mono">{r.poNumber}</TableCell>
                    <TableCell className="text-xs text-right">{r.expectedQuantity}</TableCell>
                    <TableCell className="text-xs text-right font-bold">{r.receivedQuantity}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === "Fully Received" ? "default" : "secondary"} className="text-[9px]">
                        {r.status}
                      </Badge>
                      {r.discrepancy && <Badge variant="destructive" className="text-[9px] ml-1">{r.discrepancy.type}</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Allocations */}
        {allocs.length > 0 && (
          <div>
            <h3 className="text-xs font-bold mb-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Job Allocations</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">SKU</TableHead>
                  <TableHead className="text-[10px]">Product</TableHead>
                  <TableHead className="text-[10px] text-right">Qty</TableHead>
                  <TableHead className="text-[10px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allocs.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-[10px] font-mono">{a.sku}</TableCell>
                    <TableCell className="text-xs">{a.productName}</TableCell>
                    <TableCell className="text-xs text-right">{a.quantity}</TableCell>
                    <TableCell>
                      <Badge variant={a.status === "Ready for Install" ? "default" : "outline"} className="text-[9px]">
                        {a.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* No materials */}
        {jobReceiving.length === 0 && allocs.length === 0 && (
          <div className="text-center py-6">
            <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">No materials tracked for this job yet</p>
          </div>
        )}

        {/* Flag missing */}
        {flagMode && (
          <div className="border border-destructive/30 rounded-md p-3 bg-destructive/5 space-y-2">
            <Label className="text-xs text-destructive">Describe Missing Items</Label>
            <Textarea className="text-xs" rows={2} value={flagNotes} onChange={(e) => setFlagNotes(e.target.value)} placeholder="List the missing or problematic materials..." />
          </div>
        )}

        <DialogFooter>
          {!flagMode ? (
            <>
              <Button variant="outline" className="text-destructive text-xs" size="sm" onClick={() => setFlagMode(true)}>
                <Flag className="h-3 w-3 mr-1" /> Flag Missing Items
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setFlagMode(false)}>Cancel</Button>
              <div className="flex-1" />
              <Button variant="destructive" size="sm" onClick={handleFlag}>
                <AlertTriangle className="h-3 w-3 mr-1" /> Submit Flag
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
